/**
 * Bike lane overlays for Tour Planner.
 *
 * Available (purple): OSM cycle infrastructure (lanes, tracks, cycleways).
 * Commonly used (green): signed OSM bicycle route relations (popular corridors).
 *
 * StreetLight InSight can supply true usage volumes, but it is an async paid
 * Planning API (not live map tiles) and needs REACT_APP_STREETLIGHT_API_KEY +
 * REACT_APP_STREETLIGHT_LOGIN_EMAIL. Optional precomputed GeoJSON:
 *   REACT_APP_STREETLIGHT_BIKE_GEOJSON_URL
 */

export const BIKE_AVAILABLE_COLOR = '#8b5cf6'; // purple — available infrastructure
export const BIKE_USED_COLOR = '#22c55e'; // green — commonly used / popular routes

export const BIKE_SOURCE_AVAILABLE = 'bike-lanes-available-src';
export const BIKE_SOURCE_USED = 'bike-lanes-used-src';
export const BIKE_LAYER_AVAILABLE = 'bike-lanes-available';
export const BIKE_LAYER_USED = 'bike-lanes-used';
/** Legacy single layer id (removed when dual layers are ensured). */
export const BIKE_LAYER_LEGACY = 'bike-lanes';

const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';
const EMPTY_FC = { type: 'FeatureCollection', features: [] };

function boundsKey(bounds, precision = 2) {
  if (!bounds) return '';
  const west = typeof bounds.getWest === 'function' ? bounds.getWest() : bounds.west;
  const south = typeof bounds.getSouth === 'function' ? bounds.getSouth() : bounds.south;
  const east = typeof bounds.getEast === 'function' ? bounds.getEast() : bounds.east;
  const north = typeof bounds.getNorth === 'function' ? bounds.getNorth() : bounds.north;
  const r = (n) => Number(n).toFixed(precision);
  return `${r(west)},${r(south)},${r(east)},${r(north)}`;
}

function bboxFromBounds(bounds) {
  const west = typeof bounds.getWest === 'function' ? bounds.getWest() : bounds.west;
  const south = typeof bounds.getSouth === 'function' ? bounds.getSouth() : bounds.south;
  const east = typeof bounds.getEast === 'function' ? bounds.getEast() : bounds.east;
  const north = typeof bounds.getNorth === 'function' ? bounds.getNorth() : bounds.north;
  // Overpass bbox: south,west,north,east
  return `${south},${west},${north},${east}`;
}

function wayToFeature(el, kind) {
  if (!el?.geometry?.length) return null;
  return {
    type: 'Feature',
    id: `${kind}-${el.id}`,
    properties: {
      kind,
      osmId: el.id,
      name: el.tags?.name || el.tags?.ref || '',
      highway: el.tags?.highway || '',
      cycleway: el.tags?.cycleway || '',
    },
    geometry: {
      type: 'LineString',
      coordinates: el.geometry.map((p) => [p.lon, p.lat]),
    },
  };
}

/**
 * Fetch available bike infrastructure + commonly used (signed) bike routes for a map bbox.
 * Returns { available, used, source: 'osm' | 'streetlight', key }.
 */
export async function fetchBikeLaneGeoJSON(bounds, { signal } = {}) {
  const key = boundsKey(bounds);
  if (!key) return { available: EMPTY_FC, used: EMPTY_FC, source: 'osm', key };

  // Prefer precomputed StreetLight (or any) popular-usage GeoJSON when configured.
  const streetlightUrl = process.env.REACT_APP_STREETLIGHT_BIKE_GEOJSON_URL;
  const streetlightKey = process.env.REACT_APP_STREETLIGHT_API_KEY;

  const available = await fetchOsmAvailableLanes(bounds, signal);
  let used = EMPTY_FC;
  let source = 'osm';

  if (streetlightUrl) {
    try {
      const res = await fetch(streetlightUrl, { signal });
      if (res.ok) {
        const gj = await res.json();
        if (gj?.type === 'FeatureCollection') {
          used = gj;
          source = 'streetlight';
        }
      }
    } catch (e) {
      if (e?.name === 'AbortError') throw e;
    }
  }

  if (source !== 'streetlight') {
    used = await fetchOsmPopularBikeRoutes(bounds, signal);
  }

  // StreetLight live Planning/SATC calls need credentials + async analyses —
  // not suitable for pan/zoom. Keys are detected so UI can hint setup status.
  if (streetlightKey && source !== 'streetlight') {
    source = 'osm+streetlight-pending';
  }

  return { available, used, source, key };
}

async function fetchOsmAvailableLanes(bounds, signal) {
  const bbox = bboxFromBounds(bounds);
  // Dedicated cycleways, painted lanes, tracks, and designated bicycle ways.
  const query = `
[out:json][timeout:25];
(
  way["highway"="cycleway"](${bbox});
  way["highway"="path"]["bicycle"~"designated|yes"](${bbox});
  way["cycleway"~"lane|track|opposite_lane|opposite_track|share_busway|shared_lane"](${bbox});
  way["cycleway:left"~"lane|track|share_busway|shared_lane"](${bbox});
  way["cycleway:right"~"lane|track|share_busway|shared_lane"](${bbox});
  way["cycleway:both"~"lane|track|share_busway|shared_lane"](${bbox});
  way["bicycle"="designated"](${bbox});
);
out geom;
`.trim();

  const res = await fetch(OVERPASS_URL, {
    method: 'POST',
    body: `data=${encodeURIComponent(query)}`,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    signal,
  });
  if (!res.ok) throw new Error('Bike lane Overpass fetch failed');
  const data = await res.json();
  const features = (data.elements || [])
    .map((el) => wayToFeature(el, 'available'))
    .filter(Boolean);
  return { type: 'FeatureCollection', features };
}

async function fetchOsmPopularBikeRoutes(bounds, signal) {
  const bbox = bboxFromBounds(bounds);
  // Signed bicycle route relations ≈ corridors people commonly use / are directed onto.
  const query = `
[out:json][timeout:25];
(
  relation["route"="bicycle"](${bbox});
  relation["route"="mtb"](${bbox});
);
way(r);
out geom;
`.trim();

  const res = await fetch(OVERPASS_URL, {
    method: 'POST',
    body: `data=${encodeURIComponent(query)}`,
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    signal,
  });
  if (!res.ok) throw new Error('Popular bike route Overpass fetch failed');
  const data = await res.json();
  const features = (data.elements || [])
    .filter((el) => el.type === 'way')
    .map((el) => wayToFeature(el, 'used'))
    .filter(Boolean);
  return { type: 'FeatureCollection', features };
}

export function streetlightConfigured() {
  return Boolean(
    process.env.REACT_APP_STREETLIGHT_BIKE_GEOJSON_URL ||
    process.env.REACT_APP_STREETLIGHT_API_KEY
  );
}

/** Ensure dual GeoJSON bike layers exist on a Mapbox map (purple + green). */
export function ensureBikeLaneLayers(map, beforeId = 'eco-ring') {
  if (!map) return;

  // Remove legacy single Mapbox-composite layer if present
  try {
    if (map.getLayer(BIKE_LAYER_LEGACY)) map.removeLayer(BIKE_LAYER_LEGACY);
  } catch (_) {}

  const addPair = (sourceId, layerId, color, width) => {
    if (!map.getSource(sourceId)) {
      map.addSource(sourceId, { type: 'geojson', data: EMPTY_FC });
    }
    if (!map.getLayer(layerId)) {
      const layer = {
        id: layerId,
        type: 'line',
        source: sourceId,
        minzoom: 11,
        layout: {
          visibility: 'none',
          'line-cap': 'round',
          'line-join': 'round',
        },
        paint: {
          'line-color': color,
          'line-width': ['interpolate', ['linear'], ['zoom'], 11, width[0], 16, width[1]],
          'line-opacity': 0.85,
        },
      };
      try {
        if (beforeId && map.getLayer(beforeId)) map.addLayer(layer, beforeId);
        else map.addLayer(layer);
      } catch (_) {
        try { map.addLayer(layer); } catch (__) {}
      }
    }
  };

  // Available first (under), used on top so green reads as the “hot” routes
  addPair(BIKE_SOURCE_AVAILABLE, BIKE_LAYER_AVAILABLE, BIKE_AVAILABLE_COLOR, [1.5, 3.5]);
  addPair(BIKE_SOURCE_USED, BIKE_LAYER_USED, BIKE_USED_COLOR, [2, 4.5]);
}

export function setBikeLaneVisibility(map, visible) {
  if (!map) return;
  const vis = visible ? 'visible' : 'none';
  [BIKE_LAYER_AVAILABLE, BIKE_LAYER_USED, BIKE_LAYER_LEGACY].forEach((id) => {
    try {
      if (map.getLayer(id)) map.setLayoutProperty(id, 'visibility', vis);
    } catch (_) {}
  });
}

export function setBikeLaneData(map, { available, used }) {
  if (!map) return;
  try {
    const a = map.getSource(BIKE_SOURCE_AVAILABLE);
    const u = map.getSource(BIKE_SOURCE_USED);
    if (a && available) a.setData(available);
    if (u && used) u.setData(used);
  } catch (_) {}
}
